import type { DataInputConfig } from '../model';
import type { StatTestType } from '../../dashboards/data-generator/test-generator/test-library-datasets.model';
import type { TestParameters, TestResultOrError } from './test-runner.model';
import { tCdf, tInvCdf, chiSquaredCdf, chiSquaredInvCdf, binomialCdf } from './distributions.util';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function computeTestResult(
  config: DataInputConfig,
  testType: StatTestType,
  params: TestParameters,
): TestResultOrError {
  switch (testType) {
    case 'one-sample-t':
      return computeOneSampleT(config, params);
    case 'two-sample-t':
      return computeTwoSampleT(config, params);
    case 'paired-t':
      return computePairedT(config, params);
    case 'chi-squared-independence':
      return computeChiSquaredIndependence(config, params);
    case 'chi-squared-gof':
      return computeChiSquaredGof(config, params);
    case 'sign-test':
      return computeSignTest(config, params);
  }
}

// ---------------------------------------------------------------------------
// Data extraction helpers
// ---------------------------------------------------------------------------

function extractNumericColumn(config: DataInputConfig, colIndex: number): number[] {
  const col = config.columns.filter((c) => c.colType === 'number')[colIndex];
  if (!col) {
    return [];
  }
  return config.rows.map((row) => row[col.id]).filter((v): v is number => typeof v === 'number' && !isNaN(v));
}

function extractPairedColumns(config: DataInputConfig): [number[], number[]] | null {
  const numCols = config.columns.filter((c) => c.colType === 'number');
  if (numCols.length < 2) {
    return null;
  }

  const col1 = numCols[0];
  const col2 = numCols[1];
  const a: number[] = [];
  const b: number[] = [];

  for (const row of config.rows) {
    const v1 = row[col1.id];
    const v2 = row[col2.id];
    if (typeof v1 === 'number' && !isNaN(v1) && typeof v2 === 'number' && !isNaN(v2)) {
      a.push(v1);
      b.push(v2);
    }
  }

  return [a, b];
}

function mean(arr: number[]): number {
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

function variance(arr: number[], m: number): number {
  return arr.reduce((s, v) => s + (v - m) ** 2, 0) / (arr.length - 1);
}

// ---------------------------------------------------------------------------
// p-value helpers
// ---------------------------------------------------------------------------

function tPValue(t: number, df: number, tail: 'left' | 'two' | 'right'): number {
  switch (tail) {
    case 'left':
      return tCdf(t, df);
    case 'right':
      return 1 - tCdf(t, df);
    case 'two':
      return 2 * (1 - tCdf(Math.abs(t), df));
  }
}

function tCriticalValues(alpha: number, df: number, tail: 'left' | 'two' | 'right'): number[] {
  switch (tail) {
    case 'left':
      return [tInvCdf(alpha, df)];
    case 'right':
      return [tInvCdf(1 - alpha, df)];
    case 'two':
      return [tInvCdf(alpha / 2, df), tInvCdf(1 - alpha / 2, df)];
  }
}

function chiSquaredPValue(x2: number, df: number): number {
  return 1 - chiSquaredCdf(x2, df);
}

function chiSquaredCriticalValue(alpha: number, df: number): number[] {
  return [chiSquaredInvCdf(1 - alpha, df)];
}

// ---------------------------------------------------------------------------
// One-sample t-test
// ---------------------------------------------------------------------------

function computeOneSampleT(config: DataInputConfig, params: TestParameters): TestResultOrError {
  const data = extractNumericColumn(config, 0);
  if (data.length < 2) {
    return { error: 'Enter at least 2 data values to run the test.' };
  }

  const n = data.length;
  const xBar = mean(data);
  const s2 = variance(data, xBar);
  const s = Math.sqrt(s2);

  if (s === 0) {
    return { error: 'All values are identical — cannot compute test statistic.' };
  }

  const df = n - 1;
  const t = (xBar - params.nullMean) / (s / Math.sqrt(n));
  const pValue = tPValue(t, df, params.tailType);
  const criticalValues = tCriticalValues(params.alpha, df, params.tailType);

  return {
    testType: 'one-sample-t',
    distributionType: 't',
    testStatistic: t,
    degreesOfFreedom: df,
    pValue,
    alpha: params.alpha,
    tailType: params.tailType,
    rejectNull: pValue < params.alpha,
    criticalValues,
    sampleStats: { 'x\u0304': round4(xBar), s: round4(s), n },
    nullMean: params.nullMean,
  };
}

// ---------------------------------------------------------------------------
// Two-sample t-test (Welch)
// ---------------------------------------------------------------------------

function computeTwoSampleT(config: DataInputConfig, params: TestParameters): TestResultOrError {
  const d1 = extractNumericColumn(config, 0);
  const d2 = extractNumericColumn(config, 1);

  if (d1.length < 2 || d2.length < 2) {
    return { error: 'Need at least 2 observations in each group.' };
  }

  const n1 = d1.length;
  const n2 = d2.length;
  const m1 = mean(d1);
  const m2 = mean(d2);
  const s1sq = variance(d1, m1);
  const s2sq = variance(d2, m2);

  if (s1sq === 0 && s2sq === 0) {
    return { error: 'Both groups have zero variance — cannot compute test statistic.' };
  }

  const se = Math.sqrt(s1sq / n1 + s2sq / n2);
  const t = (m1 - m2) / se;

  // Welch–Satterthwaite degrees of freedom
  const num = (s1sq / n1 + s2sq / n2) ** 2;
  const den = (s1sq / n1) ** 2 / (n1 - 1) + (s2sq / n2) ** 2 / (n2 - 1);
  const df = num / den;

  const pValue = tPValue(t, df, params.tailType);
  const criticalValues = tCriticalValues(params.alpha, df, params.tailType);

  return {
    testType: 'two-sample-t',
    distributionType: 't',
    testStatistic: t,
    degreesOfFreedom: round4(df),
    pValue,
    alpha: params.alpha,
    tailType: params.tailType,
    rejectNull: pValue < params.alpha,
    criticalValues,
    sampleStats: {
      'x\u0304\u2081': round4(m1),
      'x\u0304\u2082': round4(m2),
      's\u2081': round4(Math.sqrt(s1sq)),
      's\u2082': round4(Math.sqrt(s2sq)),
      'n\u2081': n1,
      'n\u2082': n2,
    },
    nullMean: params.nullMean,
  };
}

// ---------------------------------------------------------------------------
// Paired t-test
// ---------------------------------------------------------------------------

function computePairedT(config: DataInputConfig, params: TestParameters): TestResultOrError {
  const paired = extractPairedColumns(config);
  if (!paired) {
    return { error: 'Need two numeric columns for the paired t-test.' };
  }

  const [col1, col2] = paired;
  if (col1.length < 2) {
    return { error: 'Need at least 2 complete pairs to run the test.' };
  }

  const diffs = col1.map((v, i) => col2[i] - v);
  const n = diffs.length;
  const dBar = mean(diffs);
  const sd = Math.sqrt(variance(diffs, dBar));

  if (sd === 0) {
    return { error: 'All differences are identical — cannot compute test statistic.' };
  }

  const df = n - 1;
  const t = dBar / (sd / Math.sqrt(n));
  const pValue = tPValue(t, df, params.tailType);
  const criticalValues = tCriticalValues(params.alpha, df, params.tailType);

  return {
    testType: 'paired-t',
    distributionType: 't',
    testStatistic: t,
    degreesOfFreedom: df,
    pValue,
    alpha: params.alpha,
    tailType: params.tailType,
    rejectNull: pValue < params.alpha,
    criticalValues,
    sampleStats: { 'd\u0304': round4(dBar), s_d: round4(sd), n },
    nullMean: params.nullMean,
  };
}

// ---------------------------------------------------------------------------
// Chi-squared test for independence
// ---------------------------------------------------------------------------

function computeChiSquaredIndependence(config: DataInputConfig, params: TestParameters): TestResultOrError {
  const numCols = config.columns.filter((c) => c.colType === 'number');
  if (numCols.length < 2) {
    return { error: 'Need at least a 2\u00D72 contingency table.' };
  }

  // Extract observed counts matrix
  const R = config.rows.length;
  const C = numCols.length;

  if (R < 2 || C < 2) {
    return { error: 'Need at least a 2\u00D72 contingency table.' };
  }

  const observed: number[][] = [];
  for (let i = 0; i < R; i++) {
    const row: number[] = [];
    for (const col of numCols) {
      const v = config.rows[i][col.id];
      row.push(typeof v === 'number' ? v : 0);
    }
    observed.push(row);
  }

  const rowTotals = observed.map((row) => row.reduce((s, v) => s + v, 0));
  const colTotals = numCols.map((_, j) => observed.reduce((s, row) => s + row[j], 0));
  const grandTotal = rowTotals.reduce((s, v) => s + v, 0);

  if (grandTotal === 0) {
    return { error: 'All observed frequencies are zero.' };
  }

  let chi2 = 0;
  for (let i = 0; i < R; i++) {
    for (let j = 0; j < C; j++) {
      const expected = (rowTotals[i] * colTotals[j]) / grandTotal;
      if (expected === 0) {
        return { error: 'Expected frequency is 0 for one or more cells. Merge categories or add data.' };
      }
      chi2 += (observed[i][j] - expected) ** 2 / expected;
    }
  }

  const df = (R - 1) * (C - 1);
  const pValue = chiSquaredPValue(chi2, df);
  const criticalValues = chiSquaredCriticalValue(params.alpha, df);

  return {
    testType: 'chi-squared-independence',
    distributionType: 'chi-squared',
    testStatistic: chi2,
    degreesOfFreedom: df,
    pValue,
    alpha: params.alpha,
    tailType: 'right',
    rejectNull: pValue < params.alpha,
    criticalValues,
    sampleStats: { N: grandTotal, df },
  };
}

// ---------------------------------------------------------------------------
// Chi-squared goodness of fit
// ---------------------------------------------------------------------------

function computeChiSquaredGof(config: DataInputConfig, params: TestParameters): TestResultOrError {
  const numCols = config.columns.filter((c) => c.colType === 'number');
  if (numCols.length < 1) {
    return { error: 'Need an observed frequency column.' };
  }

  const observedCol = numCols[0];
  const observed: number[] = config.rows
    .map((row) => row[observedCol.id])
    .filter((v): v is number => typeof v === 'number');

  const k = observed.length;
  if (k < 2) {
    return { error: 'Need at least 2 categories.' };
  }

  const total = observed.reduce((s, v) => s + v, 0);
  if (total === 0) {
    return { error: 'All observed frequencies are zero.' };
  }

  const expected = total / k; // uniform assumption

  let chi2 = 0;
  for (const o of observed) {
    chi2 += (o - expected) ** 2 / expected;
  }

  const df = k - 1;
  const pValue = chiSquaredPValue(chi2, df);
  const criticalValues = chiSquaredCriticalValue(params.alpha, df);

  return {
    testType: 'chi-squared-gof',
    distributionType: 'chi-squared',
    testStatistic: chi2,
    degreesOfFreedom: df,
    pValue,
    alpha: params.alpha,
    tailType: 'right',
    rejectNull: pValue < params.alpha,
    criticalValues,
    sampleStats: { N: total, k, E: round4(expected) },
  };
}

// ---------------------------------------------------------------------------
// Sign test
// ---------------------------------------------------------------------------

function computeSignTest(config: DataInputConfig, params: TestParameters): TestResultOrError {
  const paired = extractPairedColumns(config);
  if (!paired) {
    return { error: 'Need two numeric columns for the sign test.' };
  }

  const [col1, col2] = paired;
  let positives = 0;
  let negatives = 0;

  for (let i = 0; i < col1.length; i++) {
    if (col2[i] > col1[i]) {
      positives++;
    } else if (col2[i] < col1[i]) {
      negatives++;
    }
  }

  const nNonTied = positives + negatives;
  if (nNonTied === 0) {
    return { error: 'All pairs are tied — cannot run sign test.' };
  }

  // Under H0, S+ ~ Binomial(n, 0.5)
  let pValue: number;
  switch (params.tailType) {
    case 'right':
      pValue = 1 - binomialCdf(positives - 1, nNonTied, 0.5);
      break;
    case 'left':
      pValue = binomialCdf(positives, nNonTied, 0.5);
      break;
    case 'two': {
      const pLeft = binomialCdf(positives, nNonTied, 0.5);
      const pRight = 1 - binomialCdf(positives - 1, nNonTied, 0.5);
      pValue = Math.min(2 * Math.min(pLeft, pRight), 1);
      break;
    }
  }

  return {
    testType: 'sign-test',
    distributionType: 'binomial',
    testStatistic: positives,
    degreesOfFreedom: nNonTied,
    pValue,
    alpha: params.alpha,
    tailType: params.tailType,
    rejectNull: pValue < params.alpha,
    criticalValues: [],
    sampleStats: { 'S+': positives, 'S\u2212': negatives, n: nNonTied },
  };
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function round4(v: number): number {
  return Math.round(v * 10000) / 10000;
}
